import * as React from 'react';
import { Button, Card, Col, Row, Table } from 'antd';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import moment from 'moment';
import { dateFormatter, transformTypesTracked } from '../../utils/DataFormatterUtils';
import {
	budgetsRestCrudThunksForBuyer,
	budgetsRestCrudThunksForSupplier,
} from '../../thunks/budget_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import BudgetHeaderDisplayRow from '../budget_header_display_row/BudgetHeaderDisplayRow';
import { updateNavbarState } from '../../actions/navbar_actions';
import BudgetCycleComponent from '../budget_cycle_component/BudgetCycleComponent';
import { BudgetDetailChart } from '../charts/BudgetDetailChart';
import { CSV_EXPORT_DEFAULTS, ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { DownloadOutlined } from '@ant-design/icons';
import { ExportToCsv } from 'export-to-csv';
import OWLink from '../OWLink';
import { getWorkOrderPath } from '../../utils/WorkOrderUtils';

function BudgetDetailLocationsPage({
	budget,

	currentUser,
	match,
	history,
	loading,
	userType,
	location,
}) {
	if (loading) {
		return <PageLoadingPlaceholder />;
	}
	const locationId = parseInt(match.params.locationId);
	const currency = getCurrency({ currentUser });

	const columns = [
		{
			title: 'Date',
			dataIndex: 'transactionDate',
			render: (text, record) => dateFormatter(text, 'MMM DD, YYYY'),
			sorter: (a, b) => moment(a.transactionDate).valueOf() - moment(b.transactionDate).valueOf(),
			width: 120,
		},
		{
			title: 'Title',
			dataIndex: 'title',
			render: (text, record) => (
				<span>
					<span className="bodyregular text-character-secondary">
						{record.workOrderId ? (
							<OWLink
								preserveBackLinkStates={true}
								backText="Back to Budget"
								to={`/${userType}/workOrders/detail/${record.workOrderId}/${getWorkOrderPath(
									userType,
									currentUser,
									record
								)}details`}
							>
								{`WO# ${record.workOrderId}`}
							</OWLink>
						) : (
							''
						)}
					</span>{' '}
					- {text}
				</span>
			),
		},
		{
			title: 'Type',
			dataIndex: 'type',
			render: (text, record) =>
				text === 'external_payments' ? (
					<span className="bodyregular font-semibold text-character-secondary">
						External Payment
					</span>
				) : (
					transformTypesTracked(text, true, false)
				),
			sorter: (a, b) => a.type.localeCompare(b.type),
			width: 160,
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			render: (text, record) => currency.format(text),
			sorter: (a, b) => a.amount - b.amount,
			width: 200,
			defaultSortOrder: 'descend',
		},
		{
			title: '',
			dataIndex: 'type',
			render: (text, record) => {
				const type = transformTypesTracked(text, false, true).replace(' ', '').toLowerCase();
				const path = getLinkWIthBackLinkParams(
					location,
					'Back to Budget',
					`/${userType}/${type}/detail/${record.entityId}/details`
				);
				return (
					<Button onClick={() => history.push(path)}>
						View {transformTypesTracked(text, false, false)}
					</Button>
				);
			},
		},
	];

	const locationBudget =
		locationId !== 0
			? budget.budgetPerLocations.find((budget) => locationId === budget.locationId)
			: { ...budget, budget: budget.sharedBudget };

	const items = locationBudget.budgetAccruedItems.concat(locationBudget.budgetSpentItems);

	const data =
		(locationBudget &&
			locationBudget.budgetSpentTimeSeriesData &&
			locationBudget.budgetSpentTimeSeriesData.map((d, i) => ({
				day: d.dayNumber,
				dayKey: `${d.date}`,
				consumedAmount: 0,
				totalConsumedAmount: moment().diff(moment(d.date)) >= 0 ? parseFloat(d.total) : undefined,
			}))) ||
		[];

	const budgetSpent = parseFloat(nullSafeGetOrElse('budgetSpent', locationBudget, 0));

	const budgetData = locationBudget;
	const budgetLimit = nullSafeGetOrElse(
		'budget',
		locationBudget,
		nullSafeGetOrElse('sharedBudget', locationBudget, 0)
	);
	const budgetMax = Math.max(budgetLimit, budgetSpent);
	const budgetChartConfig = {
		xAxisName: 'dayKey',
		yAxisName: 'totalConsumedAmount',
		position: 'dayKey*totalConsumedAmount',
		fillArea: false,
		cols: {
			dayKey: { alias: 'Day' },
			totalConsumedAmount: {
				min: 0,
				max: budgetMax,
				alias: 'Budget Spent',
			},
		},
		yAxisLabel: {
			textStyle: {
				fontSize: '12',
				fontFamily: 'Roboto',
				fill: 'rgba(0,0,0,0.85)',
			},
			formatter: (val, item) => {
				return currency.format(val);
			},
		},
		xAxisLabel: {
			textStyle: {
				fontSize: '12',
				fontFamily: 'Roboto',
				fill: 'rgba(0,0,0,0.85)',
			},
		},
	};

	const exportCSV = () => {
		const csvTitle = `Budget Location Activity_${budget.title}`;
		const currencyID = currency.id || 'USD';
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: ['Date', 'Title', 'Work Order #', 'Type', `Amount (${currencyID})`, 'Details'],
		});

		const data = items.map((item) => {
			const typeLabel = transformTypesTracked(item.type, false, false);
			return {
				Date: dateFormatter(item.transactionDate, 'MMM DD, YYYY'),
				Title: item.title,
				'Work Order #': item.workOrderId ? Number(item.workOrderId) : '',
				Type:
					item.type === 'external_payments'
						? 'External Payment'
						: transformTypesTracked(item.type, true, false),
				Amount: item.amount != null ? parseFloat(item.amount).toFixed(2) : '',
				Details:
					item.type === 'external_payments' || !item.entityId
						? ''
						: `=HYPERLINK("${window.location.origin}/${userType}/${transformTypesTracked(
								item.type,
								false,
								true
						  )
								.replace(/\s/g, '')
								.toLowerCase()}/detail/${item.entityId}/details", "Link to ${typeLabel}")`,
			};
		});

		csvExporter.generateCsv(data);
	};

	return (
		<Row style={{ margin: '8px 4px' }} gutter={8}>
			<Col className={'rowSpacing'} span={24}>
				<Row className={'items-baseline justify-between'}>
					<BudgetCycleComponent budget={locationBudget} />
				</Row>
			</Col>
			<Col span={24} className={'rowSpacing'}>
				<BudgetHeaderDisplayRow budget={locationBudget} currency={currency} />
			</Col>
			<Col className={'rowSpacing'} span={24}>
				<Card>
					<BudgetDetailChart
						height={500}
						chartPadding={[40, 40, 40, 40]}
						data={data || []}
						xAxisName={nullSafeGet('xAxisName', budgetChartConfig)}
						yAxisName={nullSafeGet('yAxisName', budgetChartConfig)}
						yAxisLabel={nullSafeGet('yAxisLabel', budgetChartConfig)}
						fillArea={nullSafeGet('fillArea', budgetChartConfig)}
						cols={nullSafeGet('cols', budgetChartConfig)}
						position={nullSafeGet('position', budgetChartConfig)}
						currency={currency}
						budget={budgetData}
					/>
				</Card>
			</Col>
			<Col span={24} className={'rowSpacing'}>
				<Card
					extra={
						<Button size="large" icon={<DownloadOutlined />} onClick={exportCSV}>
							Export CSV
						</Button>
					}
				>
					<Table dataSource={items} columns={columns} />
				</Card>
			</Col>
		</Row>
	);
}
const mapStateToProps = (state, ownProps) => ({
	budget: state.budgets.detail,
	loading: state.budgets.fetching,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	location: ownProps.location,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getBudget: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? budgetsRestCrudThunksForSupplier.readOne(id)
				: budgetsRestCrudThunksForBuyer.readOne(id)
		),
	deleteBudget: (budget) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? budgetsRestCrudThunksForSupplier.delete(budget)
				: budgetsRestCrudThunksForBuyer.delete(budget)
		),
	updateNavbar: (backlinkTitle, backlinkTo, navbarBackgroundColor) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, navbarBackgroundColor)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(BudgetDetailLocationsPage as any)) as any
);
